import * as React from 'react';
import { createPortal } from 'react-dom';
import { Phone, PhoneOff, Video, Users } from 'lucide-react';
import { useAnswerCall, useRejectCall } from '../queries';
import { isGroupCall, callDisplayTitle, callRoster, callType, type Call } from '../call-view';
import { useTranslation } from '@/lib/i18n/react';
import { localizedErrorMessage } from '@/lib/i18n/errors';
import { formatNumber } from '@/lib/i18n/runtime';
import { useCallActionState } from '../call-actions';

interface IncomingCallModalProps {
  call: Call;
  currentUserId?: string;
}

/**
 * Full-screen incoming-call prompt (1:1 or group). Driven by `useCallStore`'s
 * `incoming` slot, mounted app-wide via `CallOverlay`. Answer posts
 * `/calls/:id/answer` then updates the global call store to open the room; reject
 * posts `/calls/:id/reject`.
 */
export function IncomingCallModal({
  call,
  currentUserId,
}: IncomingCallModalProps) {
  const { t } = useTranslation('calls');
  const [error, setError] = React.useState<unknown>(null);
  const blocked = useCallActionState(s => !!s.action);
  const answer = useAnswerCall();
  const reject = useRejectCall();
  const group = isGroupCall(call);
  const isVideo = callType(call) === 'video';
  const title = callDisplayTitle(call, currentUserId, { call: t('call'), group: count => t('groupTitle', { count }) });
  const roster = callRoster(call);
  const busy = answer.isPending || reject.isPending || blocked;

  const handleAccept = () => {
    setError(null);
    answer.mutate(call.id, {
      onError: setError,
    });
  };

  const handleReject = () => {
    setError(null);
    reject.mutate(call.id, {
      onError: setError,
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/70 p-4">
      <div role="dialog" aria-modal="true" aria-label={t('incoming')} className="w-full max-w-sm bg-ink-900 rounded-lg shadow-2xl border border-white/10 p-6 flex flex-col items-center text-center">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 uppercase tracking-wide mb-4">
          {group ? <Users size={13} /> : isVideo ? <Video size={13} /> : <Phone size={13} />}
          {t(group ? 'incomingGroup' : isVideo ? 'incomingVideo' : 'incoming')}
        </span>

        <div className="w-20 h-20 rounded-full bg-blaze-orange flex items-center justify-center text-white font-extrabold text-3xl mb-3 animate-pulse">
          {title.slice(0, 2).toUpperCase()}
        </div>
        <h3 className="text-white font-display font-extrabold text-lg"><bdi>{title}</bdi></h3>
        {group && (
          <p className="text-white/50 text-xs mt-1">
            <bdi>{roster.map((p) => p.name).filter(Boolean).slice(0, 3).join(', ') || t('participants', { count: roster.length })}</bdi>
            {roster.length > 3 && ` +${formatNumber(roster.length - 3)}`}
          </p>
        )}

        {!!error && <p role="alert" className="mt-4 text-sm text-red-300">{localizedErrorMessage(error, 'calls', 'api.unconfirmed')}</p>}
        <div className="flex items-center gap-6 mt-8">
          <button
            type="button"
            onClick={handleReject}
            disabled={busy}
            className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white flex items-center justify-center transition-colors"
            aria-label={t('reject')}
            title={t('reject')}
          >
            <PhoneOff size={22} />
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={busy}
            className="w-14 h-14 rounded-full bg-blaze-green hover:opacity-90 disabled:opacity-50 text-white flex items-center justify-center transition-colors"
            aria-label={t('answer')}
            title={t('answer')}
          >
            <Phone size={22} />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
